import { nanoid } from 'nanoid';

import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { NotFoundError } from '#src/shared/error';

import type { PetitionGuidesDataQuery, PetitionGuidesDataQueryVariables } from './api.graphql';
import { PetitionGuidesData } from './api.graphql';

export type PetitionGuidesPageData = {
	article: NonNullable<PetitionGuidesDataQuery['article']>;
	articles: NonNullable<PetitionGuidesDataQuery['articles']>;
	coreArticles: NonNullable<PetitionGuidesDataQuery['coreArticles']>;
	aiSapId: string;
};

export async function getPetitionGuidesPageData(
	{ gql: { fetch }, errorReporter: { report } }: UtilityContext,
	slug: string,
): Promise<PetitionGuidesPageData> {
	// `coreArticles` and `articles` are non-critical navigation lists: a transient
	// failure of either (e.g. a Contentful blip) must not take down the whole page.
	// We therefore tolerate partial errors and only hard-fail when the `article`
	// itself is missing.
	const { data, errors, requestId } = await fetch<PetitionGuidesDataQuery, PetitionGuidesDataQueryVariables>({
		query: PetitionGuidesData,
		variables: {
			slug,
		},
		// rejectOnError: false — partial response: `coreArticles` and `articles` (non-critical navigation
		// lists) can fail independently of the critical `article` field; we tolerate their errors and
		// only hard-fail when `article` itself is missing.
		rejectOnError: false,
	});

	// Surface any tolerated partial errors so a genuine upstream outage stays
	// visible now that we no longer reject on them. Reported before the
	// `article` check so a failure of the critical `article` field (which nulls
	// `data.article` while populating `errors`) isn't silently turned into a 404.
	if (errors?.length) {
		void report(
			{
				error: new Error('Partial GQL error while loading Petition Guides page data'),
				params: { slug, requestId, errors },
			},
			// `error` when the critical article itself failed, `warning` when only
			// the non-critical navigation lists degraded.
			{ severity: data?.article ? 'warning' : 'error' },
		);
	}

	if (!data?.article) {
		throw new NotFoundError();
	}

	return {
		article: data.article,
		coreArticles: data.coreArticles ?? [],
		articles: data.articles ?? [],
		aiSapId: nanoid(),
	};
}
